import { useContext, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type {
  FormContextData,
  PatientTimelineResponse,
  UUID,
} from "@thalamos/aspire-common";
import {
  findFormsMerged,
  formIsInProgress,
  getLatestVersion,
} from "@thalamos/aspire-common";

import { LoggedInUserContext } from "../../../Contexts.js";
import { routeFns } from "../../../routePaths.js";
import { PatientDemographicsOutdatedBanner } from "../../FormDraft/helpers/comparePatientDemographics.js";
import {
  FormContextPdfViewer,
  previewableForms,
} from "../helpers/FormContextPdfViewer.js";
import { ParticipantsList } from "../helpers/ParticipantsList.js";
import { WorkItemStatusCard } from "../helpers/WorkItemStatusCard.js";
import { StandaloneFormButtonActions } from "./StandaloneFormButtonActions.js";
import { StandaloneFormRow } from "./StandaloneFormRow.js";
import { FormFooterSection } from "~/components/form/index.js";
import { PatientBanner } from "~/components/layout/index.js";

export function StandaloneFormPage({
  reloadFormContext,
  formContext,
  patientTimeline,
  reloadPatientTimeline,
  onReconcileClick,
}: {
  formContext: FormContextData;
  reloadFormContext: () => void;
  patientTimeline: PatientTimelineResponse | null;
  reloadPatientTimeline: () => void;
  onReconcileClick?: () => void;
}) {
  const userContext = useContext(LoggedInUserContext);
  const { user } = userContext!;
  const [pdfViewFormId, setPdfViewFormId] = useState<null | string>(null);

  const nhsNumber = patientTimeline?.patient.nhsNumber;

  const { t } = useTranslation();

  const navigate = useNavigate();

  const canAssign =
    formContext.activeTeamworkWorkItem &&
    ["accepted", "completed"].includes(
      formContext.activeTeamworkWorkItem?.status,
    );

  const showFinaliseConfirmation = (fullFormName: string) => {
    navigate(
      routeFns.formContextPageConfirmFinaliseDialog(
        formContext.id,
        formContext.patientId,
        {
          itemName: fullFormName,
        },
      ),
    );
  };

  const showMhaStatusConfirmation = (fullFormName: string) => {
    navigate(
      routeFns.formContextPageEditMhaStatusDialog(
        formContext.id,
        formContext.patientId,
        {
          itemName: fullFormName,
        },
      ),
    );
  };

  const showDeleteConfirmation = (formId: UUID) => {
    navigate(
      routeFns.formContextPageConfirmDeleteDraftDialog(
        formContext.id,
        formContext.patientId,
        { draftFormId: formId },
      ),
    );
  };

  const showRequestDeMinimisDialog = (formId: UUID, formTemplateId: string) => {
    navigate(
      routeFns.formContextPageRequestDeMinimisDialog(
        formContext.id,
        formContext.patientId,
        { formId, formTemplateId },
      ),
    );
  };

  const merged = findFormsMerged(
    patientTimeline?.mergedPatientData,
    formContext,
  );

  const hasInProgressForms = formContext.forms.some((f) => formIsInProgress(f));

  const form = formContext.forms[0];
  if (form === undefined) {
    throw new Error("Standalone form context has no form");
  }
  const latestFormData = getLatestVersion(form).data[0];

  const completionRequest = () => {
    if (form.status === "complete") {
      return { requestType: "finalise" as const };
    }
    if (form.status !== "in-progress") {
      return {};
    }
    const lastVersion = form.versions.at(-1);
    if (lastVersion === undefined) {
      throw new Error("Standalone form has no versions");
    }
    return {
      requestType: "complete" as const,
      formId: form.id,
      formPart: String(lastVersion.data.length),
    };
  };

  return (
    <Box
      data-testid="form-context:standalone"
      data-formcontextid={formContext.id}
    >
      {pdfViewFormId && (
        <FormContextPdfViewer
          inModal={true}
          forms={previewableForms(formContext.forms)}
          setFormId={setPdfViewFormId}
          formId={pdfViewFormId}
        />
      )}

      <Box sx={{ mb: 3 }}>
        {patientTimeline && (
          <PatientBanner
            patient={patientTimeline.patient}
            nhsNumber={nhsNumber ?? undefined}
            disableSticky
            isHorizontalLineHidden
            patientTimeline={patientTimeline}
            reloadPatientTimeline={reloadPatientTimeline}
            onReconcileClick={onReconcileClick}
          />
        )}
      </Box>

      <WorkItemStatusCard
        formContext={formContext}
        reloadFormContext={reloadFormContext}
        user={user}
      />

      {formContext.activeTeamworkWorkItem &&
        latestFormData !== undefined &&
        hasInProgressForms && (
          <PatientDemographicsOutdatedBanner
            patient={formContext.patient}
            currentValues={latestFormData.data}
          />
        )}

      {formContext.activeTeamworkWorkItem && (
        <StandaloneFormButtonActions
          formContext={formContext}
          user={user}
          showFinaliseConfirmation={showFinaliseConfirmation}
          showMhaStatusConfirmation={showMhaStatusConfirmation}
        />
      )}

      <StandaloneFormRow
        formContext={formContext}
        form={form}
        setPdfViewFormId={setPdfViewFormId}
        user={user}
        showDeleteConfirmation={showDeleteConfirmation}
        showFinaliseConfirmation={showFinaliseConfirmation}
        showMhaStatusConfirmation={showMhaStatusConfirmation}
        showRequestDeMinimisDialog={showRequestDeMinimisDialog}
        reloadFormContext={reloadFormContext}
        lastMerged={merged?.lastMerged}
      />

      <ParticipantsList formContext={formContext} />

      <FormFooterSection
        hideSubmit={!formContext.activeTeamworkWorkItem}
        isSticky={true}
        onSave={() => {
          navigate(
            routeFns.formContextPageSendRequest(
              formContext.id,
              formContext.patientId,
              completionRequest(),
            ),
          );
        }}
        disableSubmit={!canAssign}
        saveLabel={t("buttonLabels.send")}
        discardLabel={t("buttonLabels.activityLog")}
        onCancel={() => {
          navigate(
            routeFns.formContextPageActivityLog(
              formContext.id,
              formContext.patientId,
            ),
          );
        }}
      />
    </Box>
  );
}
